import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { backendService } from "../services/backend.service";
import { trackErrorEvent } from "../utils/analytics";
import type { DepartmentWageDataResponse, ScheduleWeek } from "../utils/models";
import { toScheduleWeek, weekEndOf } from "../utils/scheduleWeeks";
import { shareEqualDeep } from "../utils/shareEqualDeep";

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
// A cached week shows at once; past this it also refetches in the background. The poller and writes
// invalidate sooner, so this only covers changes neither of them saw.
const SCHEDULE_STALE_TIME_MS = 60 * SECOND_MS;
const SCHEDULE_GC_TIME_MS = 15 * MINUTE_MS;

/**
 * One cache entry per week, keyed by the week's first date. The week starts on the viewer's own start day,
 * and the date already says which weekday that is, so the start day isn't part of the key: a Sunday week is
 * "2026-10-11", a Monday week "2026-10-12". After the viewer changes start day the old weeks stay cached and
 * overlap the new ones, so a change to a date must reach every cached week holding it.
 */
export const scheduleKeys = {
	department: (departmentId: string) => ["schedule", departmentId] as const,
	week: (departmentId: string, weekStart: string) => ["schedule", departmentId, weekStart] as const,
} as const;

export const scheduleWagesKeys = {
	department: (departmentId: string) => ["schedule-wages", departmentId] as const,
	week: (departmentId: string, weekStart: string) => ["schedule-wages", departmentId, weekStart] as const,
} as const;

/** The panels' per-person counts over the whole schedule window: one entry per department, stale after any schedule write. */
export const staffShiftStatsKeys = {
	department: (departmentId: string) => ["staff-shift-stats", departmentId] as const,
} as const;

/** Time-off answers that read a nurse's whole history: one prefix per department, so a time-off edit marks them all stale. */
export const timeOffHistoryKeys = {
	department: (departmentId: string) => ["time-off-history", departmentId] as const,
	rdoDays: (departmentId: string, draftStart: string | null) => ["time-off-history", departmentId, "rdo-days", draftStart] as const,
	longTimeOff: (departmentId: string, fromDate: string) => ["time-off-history", departmentId, "long-time-off", fromDate] as const,
} as const;

export const scheduleWeekQuery = (departmentId: string, weekStart: string) =>
	queryOptions({
		queryKey: scheduleKeys.week(departmentId, weekStart),
		queryFn: async (): Promise<ScheduleWeek> => toScheduleWeek((await backendService.getSchedule(departmentId, weekStart, weekEndOf(weekStart))).data),
		structuralSharing: shareEqualDeep,
		staleTime: SCHEDULE_STALE_TIME_MS,
		gcTime: SCHEDULE_GC_TIME_MS,
	});

export const scheduleWagesWeekQuery = (departmentId: string, weekStart: string) =>
	queryOptions({
		queryKey: scheduleWagesKeys.week(departmentId, weekStart),
		queryFn: async (): Promise<DepartmentWageDataResponse> => (await backendService.getScheduleWages(departmentId, weekStart, weekEndOf(weekStart))).data,
		structuralSharing: shareEqualDeep,
		staleTime: SCHEDULE_STALE_TIME_MS,
		gcTime: SCHEDULE_GC_TIME_MS,
	});

// A prefetch fetches a week nobody opened yet, so a failure is tracked but not shown: opening that week asks again
// and shows its own error state. A week already cached and fresh isn't asked for.
export const prefetchScheduleWeek = async (queryClient: QueryClient, departmentId: string, weekStart: string): Promise<void> => {
	try {
		await queryClient.query(scheduleWeekQuery(departmentId, weekStart));
	} catch (error) {
		trackErrorEvent("schedule_week_prefetch_error", error, { department_id: departmentId, week_start: weekStart });
	}
};

export const prefetchScheduleWagesWeek = async (queryClient: QueryClient, departmentId: string, weekStart: string): Promise<void> => {
	try {
		await queryClient.query(scheduleWagesWeekQuery(departmentId, weekStart));
	} catch (error) {
		trackErrorEvent("schedule_wages_prefetch_error", error, { department_id: departmentId, week_start: weekStart });
	}
};
